import React, { useState } from "react";
import { Pin, Edit2, Trash2, Calendar, User, Check, X } from "lucide-react";
import {
  Card,
  CardContent,
  Badge,
  IconButton,
} from "./ui";

const CATEGORY_BORDER_COLORS = {
  GENERAL: "border-l-neutral-400 dark:border-l-neutral-500",
  EXAM: "border-l-primary-500",
  HOLIDAY: "border-l-danger-500",
  EVENT: "border-l-purple-500",
  FEE: "border-l-warning-500",
  URGENT: "border-l-danger-600 bg-danger-50/30 dark:bg-danger-500/10",
};

const CATEGORY_BADGE_VARIANTS = {
  GENERAL: "default",
  EXAM: "primary",
  HOLIDAY: "danger",
  EVENT: "purple",
  FEE: "warning",
  URGENT: "danger",
};

const NoticeCard = ({ notice, canManage, onEdit, onDelete, onPin, onClick }) => {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const isNearExpiry = (expiryDate) => {
    if (!expiryDate) return false;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 7;
  };

  return (
    <Card
      className={`group relative border-l-4 ${
        CATEGORY_BORDER_COLORS[notice.category] || "border-l-neutral-300"
      } cursor-pointer transition-all duration-200 hover:shadow-md`}
      onClick={() => onClick(notice)}
    >
      <CardContent className="p-4 sm:p-5">
        {/* Pinned Badge */}
        {notice.isPinned && (
          <div className="absolute right-4 top-4">
            <Badge variant="warning" className="gap-1 font-semibold text-[11px]">
              <Pin size={11} className="fill-current shrink-0" />
              <span>Pinned</span>
            </Badge>
          </div>
        )}

        {/* Category & Audience Tags */}
        <div className="mb-3 flex flex-wrap items-center gap-1.5 pr-20">
          <Badge variant={CATEGORY_BADGE_VARIANTS[notice.category] || "default"}>
            {notice.category}
          </Badge>

          {notice.targetAudience?.map((audience) => (
            <Badge key={audience} variant="secondary" className="text-[11px]">
              {audience}
            </Badge>
          ))}

          {!notice.isActive && (
            <Badge variant="default" className="bg-neutral-200 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300">
              Inactive
            </Badge>
          )}
        </div>

        {/* Title */}
        <h3 className="mb-2 line-clamp-1 text-base font-bold text-neutral-900 dark:text-neutral-100 group-hover:text-primary-600 transition-colors">
          {notice.title}
        </h3>

        {/* Content Snippet */}
        <p className="mb-4 line-clamp-3 whitespace-pre-wrap text-xs sm:text-sm text-neutral-600 dark:text-neutral-300 leading-relaxed">
          {notice.content}
        </p>

        {/* Footer Meta & Actions */}
        <div className="flex flex-col gap-3 pt-3 border-t border-neutral-100 dark:border-neutral-800 sm:flex-row sm:items-center sm:justify-between text-xs text-neutral-500 dark:text-neutral-400">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
            <span className="flex items-center gap-1">
              <User size={13} className="shrink-0 text-neutral-400 dark:text-neutral-500" />
              <span className="truncate max-w-[120px] sm:max-w-none">
                {notice.createdBy?.name || "System"}
              </span>
            </span>

            <span className="flex items-center gap-1">
              <Calendar size={13} className="shrink-0 text-neutral-400 dark:text-neutral-500" />
              <span>{formatDate(notice.createdAt)}</span>
            </span>

            {notice.expiresAt && (
              <span
                className={`font-medium ${
                  isNearExpiry(notice.expiresAt)
                    ? "rounded bg-danger-50 dark:bg-danger-500/15 px-1.5 py-0.5 text-danger-600 dark:text-danger-500"
                    : "text-neutral-500 dark:text-neutral-400"
                }`}
              >
                Expires: {formatDate(notice.expiresAt)}
              </span>
            )}
          </div>

          {/* Management Controls */}
          {canManage && (
            <div
              className="flex items-center gap-1 self-end sm:self-auto shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <IconButton
                type="button"
                variant={notice.isPinned ? "warning" : "ghost"}
                size="sm"
                onClick={() => onPin(notice._id)}
                title={notice.isPinned ? "Unpin Notice" : "Pin Notice"}
              >
                <Pin size={14} />
              </IconButton>

              <IconButton
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onEdit(notice)}
                title="Edit Notice"
              >
                <Edit2 size={14} className="text-primary-600 dark:text-primary-400" />
              </IconButton>

              {showConfirmDelete ? (
                <div className="flex items-center gap-0.5 rounded-lg border border-danger-200 dark:border-danger-500/30 bg-danger-50 dark:bg-danger-500/10 p-0.5">
                  <IconButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 text-danger-600 hover:bg-danger-100"
                    onClick={() => {
                      onDelete(notice._id);
                      setShowConfirmDelete(false);
                    }}
                    title="Confirm Delete"
                  >
                    <Check size={14} />
                  </IconButton>
                  <IconButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                    onClick={() => setShowConfirmDelete(false)}
                    title="Cancel"
                  >
                    <X size={14} />
                  </IconButton>
                </div>
              ) : (
                <IconButton
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowConfirmDelete(true)}
                  title="Delete Notice"
                >
                  <Trash2 size={14} className="text-danger-600" />
                </IconButton>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default NoticeCard;